import { unsafeCharactersRegex } from 'Utils/formats';

const NAME_MAXLENGTH = 128;

export default class filestackHelper {
  static getTitleSafe(filename) {
    const fileTitle = (filename || '').indexOf('.') > -1 ? filename.substring(0, filename.lastIndexOf('.')) : (filename || '');
    const fileTitleSafeCharacters = fileTitle.replace(unsafeCharactersRegex, '');
    const fileTitleSafeCharactersAndLength = fileTitleSafeCharacters.length > NAME_MAXLENGTH
      ? fileTitleSafeCharacters.substr(0, NAME_MAXLENGTH)
      : fileTitleSafeCharacters;
    return fileTitleSafeCharactersAndLength;
  }

  static getExtensionSafe(filename) {
    const fileExtension = (filename || '').indexOf('.') > -1 ? (filename).substr(filename.lastIndexOf('.') + 1) : '';
    const fileExtensionSafeCharacters = fileExtension.replace(unsafeCharactersRegex, '');
    const fileExtensionSafeCharactersAndLength = fileExtensionSafeCharacters.length > NAME_MAXLENGTH
      ? fileExtensionSafeCharacters.substr(0, NAME_MAXLENGTH)
      : fileExtensionSafeCharacters;
    return fileExtensionSafeCharactersAndLength.toLowerCase();
  }

  static convertFileToBlob(file) {
    // Returns a Blob object from a File using async/await
    return (async () => {
      if (file instanceof File) {
        return new Blob([file], { type: file.type });
      }
      throw new Error('Input is not a File or Blob object');
    })();
  }

  static getImageDimensions(img) {
    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    return { width, height };
  }

  /**
   * Progressive step-down resize (halve repeatedly) to preserve detail.
   * Browsers can look blurry when resizing large->small in a single drawImage.
   */
  static stepDownResize(source, targetWidth, targetHeight) {
    const { width: sourceWidth, height: sourceHeight } = filestackHelper.getImageDimensions(source);
    if (!Number.isFinite(sourceWidth) || !Number.isFinite(sourceHeight) || sourceWidth <= 0 || sourceHeight <= 0) {
      throw new Error('Invalid image dimensions');
    }

    let canvas = document.createElement('canvas');
    canvas.width = sourceWidth;
    canvas.height = sourceHeight;
    let ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, sourceWidth, sourceHeight);

    let currentWidth = sourceWidth;
    let currentHeight = sourceHeight;

    while (currentWidth * 0.5 > targetWidth || currentHeight * 0.5 > targetHeight) {
      const nextWidth = Math.max(targetWidth, Math.round(currentWidth * 0.5));
      const nextHeight = Math.max(targetHeight, Math.round(currentHeight * 0.5));

      const stepCanvas = document.createElement('canvas');
      stepCanvas.width = nextWidth;
      stepCanvas.height = nextHeight;
      const stepCtx = stepCanvas.getContext('2d');
      if (!stepCtx) {
        throw new Error('Failed to get canvas context');
      }
      stepCtx.imageSmoothingEnabled = true;
      stepCtx.imageSmoothingQuality = 'high';
      stepCtx.drawImage(canvas, 0, 0, nextWidth, nextHeight);

      canvas = stepCanvas;
      ctx = stepCtx;
      currentWidth = nextWidth;
      currentHeight = nextHeight;
    }

    if (currentWidth !== targetWidth || currentHeight !== targetHeight) {
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = targetWidth;
      finalCanvas.height = targetHeight;
      const finalCtx = finalCanvas.getContext('2d');
      if (!finalCtx) {
        throw new Error('Failed to get canvas context');
      }
      finalCtx.imageSmoothingEnabled = true;
      finalCtx.imageSmoothingQuality = 'high';
      finalCtx.drawImage(canvas, 0, 0, targetWidth, targetHeight);
      return finalCanvas;
    }

    return canvas;
  }

  /**
   * Lightweight unsharp mask (radius=1) to recover perceived sharpness after downscaling.
   * This mimics what many image CDNs do after resizing.
   */
  static applyUnsharpMask(canvas, amount = 0.45, threshold = 1) {
    if (!canvas) {
      return;
    }
    const width = canvas.width;
    const height = canvas.height;
    if (!Number.isFinite(width) || !Number.isFinite(height) || width < 2 || height < 2) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    const stride = width * 4;

    // Separable blur with kernel [1, 2, 1] / 4 (approx Gaussian, radius=1)
    const temp = new Uint8ClampedArray(data.length);
    const blurred = new Uint8ClampedArray(data.length);

    const blur3 = (a, b, c) => Math.round((a + (2 * b) + c) / 4);

    for (let y = 0; y < height; y += 1) {
      const row = y * stride;
      for (let x = 0; x < width; x += 1) {
        const xm1 = x === 0 ? 0 : x - 1;
        const xp1 = x === width - 1 ? width - 1 : x + 1;
        const i = row + x * 4;
        const im1 = row + xm1 * 4;
        const ip1 = row + xp1 * 4;
        temp[i] = blur3(data[im1], data[i], data[ip1]);
        temp[i + 1] = blur3(data[im1 + 1], data[i + 1], data[ip1 + 1]);
        temp[i + 2] = blur3(data[im1 + 2], data[i + 2], data[ip1 + 2]);
        temp[i + 3] = data[i + 3];
      }
    }

    for (let y = 0; y < height; y += 1) {
      const ym1 = y === 0 ? 0 : y - 1;
      const yp1 = y === height - 1 ? height - 1 : y + 1;
      for (let x = 0; x < width; x += 1) {
        const i = (y * width + x) * 4;
        const im1 = (ym1 * width + x) * 4;
        const ip1 = (yp1 * width + x) * 4;
        blurred[i] = blur3(temp[im1], temp[i], temp[ip1]);
        blurred[i + 1] = blur3(temp[im1 + 1], temp[i + 1], temp[ip1 + 1]);
        blurred[i + 2] = blur3(temp[im1 + 2], temp[i + 2], temp[ip1 + 2]);
        blurred[i + 3] = data[i + 3];
      }
    }

    const t = Math.max(0, threshold);
    const a = Math.max(0, amount);
    for (let i = 0; i < data.length; i += 4) {
      // Preserve alpha as-is; sharpen RGB channels only.
      for (let c = 0; c < 3; c += 1) {
        const orig = data[i + c];
        const blur = blurred[i + c];
        const diff = orig - blur;
        if (Math.abs(diff) > t) {
          let val = orig + a * diff;
          if (val < 0) {
            val = 0;
          } else if (val > 255) {
            val = 255;
          }
          data[i + c] = Math.round(val);
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);
  }

  /**
   * Unified resize implementation.
   * - mode='fit': preserve aspect ratio within bounds (no upscale)
   * - mode='stretch': force exact dimensions (may distort; no upscale)
   * @param {boolean} [convertPngToJpeg=false] - When true, PNG inputs are converted to JPEG (transparency composited over white).
   */
  static resizeImageFileSmart(file, maxWidth, maxHeight, convertPngToJpeg = false, mode = 'fit') {
    return new Promise((resolve, reject) => {
      if (!(file instanceof File)) {
        reject(new Error('Input is not a File object'));
        return;
      }

      // GIFs (especially animated) cannot be reliably resized client-side via canvas.
      // Drawing a GIF to canvas typically flattens it to a single frame, and encoding back to
      // image/gif is not consistently supported across browsers.
      if (file.type === 'image/gif') {
        resolve(file);
        return;
      }

      if (!Number.isFinite(maxWidth) || !Number.isFinite(maxHeight) || maxWidth <= 0 || maxHeight <= 0) {
        reject(new Error('Invalid target dimensions'));
        return;
      }

      const img = new window.Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        try {
          const { width: originalWidth, height: originalHeight } = filestackHelper.getImageDimensions(img);
          if (!Number.isFinite(originalWidth) || !Number.isFinite(originalHeight) || originalWidth <= 0 || originalHeight <= 0) {
            reject(new Error('Invalid image dimensions'));
            return;
          }

          const outWidth = Math.round(maxWidth);
          const outHeight = Math.round(maxHeight);

          let innerWidth;
          let innerHeight;
          let effectiveScale;
          if (mode === 'stretch') {
            // Force exact output dimensions (may distort), but never upscale.
            innerWidth = Math.max(1, Math.min(outWidth, originalWidth));
            innerHeight = Math.max(1, Math.min(outHeight, originalHeight));
            effectiveScale = Math.min(innerWidth / originalWidth, innerHeight / originalHeight);
          } else {
            // Fit within bounds while preserving aspect ratio; never upscale.
            effectiveScale = Math.min(1, outWidth / originalWidth, outHeight / originalHeight);
            innerWidth = Math.max(1, Math.round(originalWidth * effectiveScale));
            innerHeight = Math.max(1, Math.round(originalHeight * effectiveScale));
          }

          const innerCanvas = filestackHelper.stepDownResize(img, innerWidth, innerHeight);

          if (effectiveScale < 1) {
            const sharpenAmount = Math.min(0.9, 0.45 + (1 - effectiveScale) * 0.6);
            filestackHelper.applyUnsharpMask(innerCanvas, sharpenAmount, 1);
            if (effectiveScale < 0.6) {
              filestackHelper.applyUnsharpMask(innerCanvas, Math.min(0.5, sharpenAmount * 0.5), 2);
            }
          }

          // Optionally convert PNG to JPEG when the flag is set.
          const mimeType = (convertPngToJpeg && file.type === 'image/png' ? 'image/jpeg' : file.type) || 'image/jpeg';
          const isLossy = mimeType === 'image/jpeg';
          const effectiveQuality = 0.95;

          const encodeCanvas = (canvas) => {
            const onBlob = (blob) => {
              if (!blob) {
                reject(new Error('Failed to encode canvas'));
                return;
              }
              // Rename .png → .jpg when we're converting to JPEG.
              const outputName = (mimeType === 'image/jpeg' && file.type === 'image/png')
                ? file.name.replace(/\.png$/i, '.jpg')
                : file.name;
              resolve(new File([blob], outputName, { type: mimeType, lastModified: Date.now() }));
            };

            const shouldFlattenPngTransparency = mimeType === 'image/jpeg' && file.type === 'image/png';

            if (isLossy && shouldFlattenPngTransparency) {
              // Composite over white before JPEG encoding to flatten PNG transparency.
              const flatCanvas = document.createElement('canvas');
              flatCanvas.width = canvas.width;
              flatCanvas.height = canvas.height;
              const flatCtx = flatCanvas.getContext('2d');
              if (!flatCtx) {
                reject(new Error('Failed to get canvas context'));
                return;
              }
              flatCtx.fillStyle = '#ffffff';
              flatCtx.fillRect(0, 0, flatCanvas.width, flatCanvas.height);
              flatCtx.drawImage(canvas, 0, 0);
              flatCanvas.toBlob(onBlob, mimeType, effectiveQuality);
            } else if (isLossy) {
              canvas.toBlob(onBlob, mimeType, effectiveQuality);
            } else {
              canvas.toBlob(onBlob, mimeType);
            }
          };

          encodeCanvas(innerCanvas);
        } catch (e) {
          reject(e);
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image for resizing'));
      };
      img.src = objectUrl;
    });
  }

  /**
   * Resize an image file to fit within the specified max width/height while preserving aspect ratio.
   * Never upscales.
   * @param {File} file - The image file to resize
   * @param {number} maxWidth - The maximum output width
   * @param {number} maxHeight - The maximum output height
   * @param {boolean} [convertPngToJpeg=false] - When true, PNG inputs are converted to JPEG.
   * @returns {Promise<File>} - The resized image as a File
   */
  static resizeImageFileToFit(file, maxWidth, maxHeight, convertPngToJpeg = false) {
    return filestackHelper.resizeImageFileSmart(file, maxWidth, maxHeight, convertPngToJpeg, 'fit');
  }

  /**
   * Resize an image file to the specified width and height using canvas.
   * Returns a Promise that resolves to a new File object.
   * @param {File} file - The image file to resize
   * @param {number} width - The target width
   * @param {number} height - The target height
   * @param {boolean} [convertPngToJpeg=false] - When true, PNG inputs are converted to JPEG.
   * @returns {Promise<File>} - The resized image as a File
   */
  static resizeImageFile(file, width, height, convertPngToJpeg = false) {
    return filestackHelper.resizeImageFileSmart(file, width, height, convertPngToJpeg, 'stretch');
  }
}
